import { useEffect, useRef, useState } from "react";

const FALLBACK_MS = 8000;
const synth = typeof window !== "undefined" ? window.speechSynthesis : null;

const spanishVoice = () => {
  const voices = synth?.getVoices() || [];
  return voices.find((v) => v.lang === "es-ES") || voices.find((v) => v.lang?.startsWith("es")) || null;
};

// Plays one narration src at a time. Without src (browser voice mode) or if the audio can't load,
// it reads `text` with the computer's own voice.
export function useNarration({ src, text, auto, onEnded }) {
  const audioRef = useRef(null);
  const endedRef = useRef(onEnded);
  const autoRef = useRef(auto);
  const textRef = useRef(text);
  const speakingRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  endedRef.current = onEnded;
  autoRef.current = auto;
  textRef.current = text;

  const stopSpeech = () => {
    speakingRef.current = false;
    synth?.cancel();
  };

  const speak = () => {
    if (!synth || !textRef.current) return false;
    stopSpeech();
    const u = new SpeechSynthesisUtterance(textRef.current);
    u.lang = "es-ES";
    u.voice = spanishVoice();
    u.rate = 1.02;
    u.onstart = () => setPlaying(true);
    u.onend = () => {
      if (!speakingRef.current) return;
      speakingRef.current = false;
      setPlaying(false);
      endedRef.current?.();
    };
    speakingRef.current = true;
    synth.speak(u);
    return true;
  };

  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    audioRef.current = a;
    let timer = null;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => {
      setPlaying(false);
      endedRef.current?.();
    };
    const onError = () => {
      setPlaying(false);
      a.dataset.failed = "1";
      if (!autoRef.current) return;
      if (!speak()) timer = setTimeout(() => endedRef.current?.(), FALLBACK_MS);
    };
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onError);
    return () => {
      clearTimeout(timer);
      a.pause();
      stopSpeech();
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    stopSpeech();
    a.pause();
    if (!src) {
      a.dataset.failed = "1";
      setPlaying(false);
      if (auto && text) speak();
      return;
    }
    delete a.dataset.failed;
    a.src = src;
    if (auto) a.play().catch(() => setPlaying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, text, auto]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.dataset.failed) {
      if (speakingRef.current) {
        stopSpeech();
        setPlaying(false);
      } else speak();
      return;
    }
    if (a.paused) a.play().catch(() => setPlaying(false));
    else a.pause();
  };

  return { playing, toggle };
}
